import { Button, Space, Tag } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/views/stores";
import { APPLICATIONS } from "@zhihaoscm/meta";
import { BaseItem as ZhBaseItem, DetailHeader as ZhDetailHeader, Grid as ZhGrid } from "zhihao-ui-react";
import { deleteApplication, getApplicationSecretKey } from "./ApplicationPage";
import { loadApplication } from "./ApplicationFormPage";
import type { ApplicationRow } from "./types";

export interface ApplicationDetailPageProps {
    deleteApplication?: (id: number | string) => Promise<void>;
    getSecretKey?: (id: number | string) => Promise<string>;
    loadApplication?: (id: number | string) => Promise<ApplicationRow>;
}

/** 统一 Vue 详情字段的空字符串缺省值。 */
const formatDetailValue = (value?: string | number | null) =>
    value === null || value === undefined || value === "" ? DEFAULT_VALUE : value;

/** 应用详情页，保留旧 Vue 详情路由和显式查看 SK 行为。 */
export function ApplicationDetailPage({
    deleteApplication: remove = deleteApplication,
    getSecretKey = getApplicationSecretKey,
    loadApplication: load = loadApplication
}: ApplicationDetailPageProps) {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    // 沿用 Vue 全局封禁态，详情页写操作仍展示但不可触发。
    const banButton = useUserStore(state => state.banButton);
    const [application, setApplication] = useState<ApplicationRow | null>(null);
    const [secretKey, setSecretKey] = useState<string | null>(null);
    // 记录明文 SK 是否处于显式展示状态，保持旧 Vue 的查看/隐藏切换行为。
    const [secretKeyVisible, setSecretKeyVisible] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!id) return;
        let disposed = false;
        void load(id)
            .then(result => {
                if (!disposed) setApplication(result);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("应用信息加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, load]);

    const handleShowSecret = async () => {
        if (!id) return;
        if (secretKeyVisible) {
            setSecretKeyVisible(false);
            return;
        }
        try {
            setSecretKey(await getSecretKey(id));
            setSecretKeyVisible(true);
        } catch {
            setErrorMessage("应用 SK 获取失败");
        }
    };

    // 删除成功后返回上一级，保持 Vue 详情页的接口参数和导航行为。
    const handleDelete = async () => {
        if (!id) return;
        try {
            await remove(Number(id));
            navigate(-1);
        } catch {
            setErrorMessage("应用删除失败");
        }
    };

    return (
        <section
            className="detail-page-transparent flex h-full min-h-full flex-col"
            aria-label="application-detail-page"
        >
            <div className="detail-json">
                <div className="detail-json__block">
                    <ZhDetailHeader
                        className="mb-6"
                        leftInfos={[{ label: "应用ID", value: formatDetailValue(application?.id ?? id) }]}
                        title="应用详情"
                    />
                    {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                    {application ? (
                        <ZhGrid className="mt-6" cols={4} gap={[32, 20]}>
                            <ZhBaseItem label="应用名称" value={formatDetailValue(application.appName)} />
                            <ZhBaseItem label="AK (access key)" value={formatDetailValue(application.accessKey)} />
                            <ZhBaseItem label="SK (secret key)">
                                {formatDetailValue(secretKeyVisible ? secretKey : application.secretKey)}
                                <Button className="ml-2" type="link" onClick={() => void handleShowSecret()}>
                                    {secretKeyVisible ? "隐藏 SK" : "查看 SK"}
                                </Button>
                            </ZhBaseItem>
                            <ZhBaseItem label="回调地址" value={formatDetailValue(application.callbackAddress)} />
                        </ZhGrid>
                    ) : null}
                </div>
                {application ? (
                    <div className="detail-json__block">
                        <div className="detail-json__block-title">
                            <div className="detail-json__section-title">功能点</div>
                        </div>
                        {application.applications?.length ? (
                            <Space wrap>
                                {application.applications.map(item => (
                                    <Tag key={item}>{APPLICATIONS.getIntroByCode(item) || item}</Tag>
                                ))}
                            </Space>
                        ) : (
                            DEFAULT_VALUE
                        )}
                    </div>
                ) : null}
            </div>
            <Space className="operation-fixed-btn" size={0}>
                <Button
                    disabled={banButton}
                    type="primary"
                    onClick={() => void navigate(`/manageCenter/appManagement/applicationEdit/${id}`)}
                >
                    编辑
                </Button>
                <Button disabled={banButton} type="primary" onClick={() => void handleDelete()}>
                    删除
                </Button>
                <Button type="primary" onClick={() => void navigate(-1)}>
                    返回
                </Button>
            </Space>
        </section>
    );
}

export default ApplicationDetailPage;
